
import {
  computed,
  defineComponent,
  inject,
  nextTick,
  onMounted,
  onUnmounted,
  ref,
  watch,
} from "vue";
import MessageIn from "@/components/messenger-parts/MessageIn.vue";
import MessageOut from "@/components/messenger-parts/MessageOut.vue";
import { MessageAPI } from "@/services/api/Message.service";
import Button from "primevue/button";
import { SmsAPI } from "@/services/api/Sms.service";
import Footer from "@/views/sms/components/detail/Footer.vue";
import { ICurrentUser } from "@/interfaces/iCurrentUser";
import {
  FETCH_SMS_NOTIFICATION,
  NOTIFICATION_STORE,
  GET_SMS_NOTIFICATION,
} from "@/store/notification/types";
import { ChatMessage } from "@/interfaces/iMessage";
import { Emitter } from "mitt";
import { useStore } from "vuex";
import {
  GET_CONVERSATION_CHANGED,
  MESSAGE_STORE,
  SMS_DATA_CHANGED,
  GET_TOGGLE_CONVERSATION_ID,
  FETCH_MESSAGES,
  GET_CONVERSATION_MESSAGES,
} from "@/store/message/types";
interface KTMessage {
  type: string;
  name?: string;
  image: string;
  time: string;
  text: string;
  own: boolean;
  status: string;
}
type Events = {
  "sms-list-refresh": boolean;
};

export default defineComponent({
  name: "upgrade-to-pro",
  components: {
    MessageIn,
    MessageOut,
    Button,
    Footer,
  },
  setup() {
    const messagesRef = ref<null | HTMLElement>(null);
    const messagesInRef = ref<null | HTMLElement>(null);
    const messagesOutRef = ref<null | HTMLElement>(null);
    const title = ref("");
    // const messages = ref<Array<KTMessage>>([]);
    const conversations = ref();
    const storeCID = ref();
    const currentConversationIndex = ref(-1);
    const store = useStore();
    const SMS_Changed = `${MESSAGE_STORE}/${GET_CONVERSATION_CHANGED}`;
    const SELECTED_ID = `${MESSAGE_STORE}/${GET_TOGGLE_CONVERSATION_ID}`;
    const smsChangedGetter = computed(() => store.getters[SMS_Changed]);
    const idForSelectedItem = computed(() => store.getters[SELECTED_ID]);
    const smsCount = computed(() => {
      const key = `${NOTIFICATION_STORE}/${GET_SMS_NOTIFICATION}`;
      return store.getters[key] as number;
    });
    const messages = computed(() => {
      const currentWebId =
        conversations.value?.[currentConversationIndex.value]?.webId || "";
      if (!currentWebId) return [];
      const key = `${MESSAGE_STORE}/${GET_CONVERSATION_MESSAGES}`;
      return store.getters[key](currentWebId) as ChatMessage[];
    });
    const emitter: Emitter<Events> | undefined = inject("emitter");
    emitter?.on("sms-list-refresh", async () => {
      await getSmsData();
      if (currentConversationIndex.value >= 0) {
        await loadConversationMsgs(currentConversationIndex.value);
      }
    });
    let prevMessageLength = 0;
    watch(
      messages,
      async (newMessages) => {
        if (newMessages.length > prevMessageLength && messagesRef.value) {
          // New message added (assuming store appends on real-time)
          await nextTick();
          scrollBottom();
        }
        prevMessageLength = newMessages.length;
      },
      { immediate: true }
    );
    onMounted(async () => {
      await getSmsData();
      scrollBottom();
      document
        .getElementById("kt-data-messages-scroll")
        ?.addEventListener("scroll", async () => {
          const element = document.getElementById("kt-data-messages-scroll");
          if (
            element &&
            element?.scrollTop === 0 &&
            messages.value.length > 0
          ) {
            const oldScrollHeight = element.scrollHeight;
            if (oldScrollHeight && element?.scrollTop) {
              element.scrollTop = element?.scrollHeight - oldScrollHeight;
            }
          }
        });
      emitter?.on("sms-list-refresh", async () => {
        await getSmsData();
        if (currentConversationIndex.value >= 0) {
          await loadConversationMsgs(currentConversationIndex.value);
        }
      });
    });

    const scrollBottom = () => {
      setTimeout(() => {
        if (document.getElementById("kt-data-messages-scroll")) {
          const x = document.getElementById("kt-data-messages-scroll");
          if (x) {
            x.scrollTo(0, x.scrollHeight);
          }
        }
      }, 100);
    };

    watch(smsChangedGetter, (newValue, oldValue) => {
      getSmsData();
    });

    watch(idForSelectedItem, (newValue, oldValue) => {
      const targetIndex = conversations.value.findIndex(
        (obj) => obj.webId == newValue
      );
      if (targetIndex !== -1) {
        currentConversationIndex.value = targetIndex;
        loadConversationMsgs(targetIndex);
      }
      if (messagesRef.value) {
        messagesRef.value.scrollTop = messagesRef.value.scrollHeight;
      }
      scrollBottom();
    });

    const currentUser = computed(() => {
      return store.getters.currentUser as ICurrentUser;
    });
    const getSmsData = () => {
      SmsAPI.getInstance()
        .getSmsList({
          status: "OPEN",
        })
        .then(async (response) => {
          conversations.value = response.content;
          if (conversations.value.length > 0) {
            title.value = conversations.value[0].title;
            currentConversationIndex.value = 0;
            await loadConversationMsgs(0); // Now dispatches store fetch
          }
        })
        .catch((err) => {
          console.error(err);
        });
    };

    const newMessageText = ref("");

    const previousConversation = () => {
      currentConversationIndex.value--;
      loadConversationMsgs(currentConversationIndex.value);
      if (messagesRef.value) {
        messagesRef.value.scrollTop = messagesRef.value.scrollHeight;
      }
    };

    const loadConversationMsgs = async (conversationIndex: number) => {
      storeCID.value = conversationIndex;
      title.value = conversations.value[conversationIndex]?.title;
      const currentWebId = conversations.value[conversationIndex]?.webId;
      if (!currentWebId) return;

      if (conversationIndex != 0) {
        await MessageAPI.getInstance().markMessagesRead(currentWebId);
        await store.dispatch(`${NOTIFICATION_STORE}/${FETCH_SMS_NOTIFICATION}`);
      }

      await store.dispatch(`${MESSAGE_STORE}/${FETCH_MESSAGES}`, {
        conversationId: currentWebId,
        currentUserId: currentUser.value.webId,
        searchTerm: "",
      });

      await nextTick();
      scrollBottom();
    };
    watch(smsCount, async () => {
      if (storeCID.value) loadConversationMsgs(storeCID.value);
    });
    const nextConversation = () => {
      currentConversationIndex.value++;
      loadConversationMsgs(currentConversationIndex.value);
      if (messagesRef.value) {
        messagesRef.value.scrollTop = messagesRef.value.scrollHeight;
      }
    };
    const observeClickOnMsg = () => {
      document.body.addEventListener("click", async (event) => {
        const clickedElement = event.target as HTMLElement;
        if (clickedElement.classList.contains("kt_drawer_chat_icon")) {
          await getSmsData();
          const selectedId = store.getters[SELECTED_ID];
          if (selectedId && conversations.value) {
            const targetIndex = conversations.value.findIndex(
              (obj) => obj.webId == selectedId
            );
            if (targetIndex !== -1) {
              currentConversationIndex.value = targetIndex;
              await loadConversationMsgs(targetIndex);
            }
          } else if (conversations.value?.length > 0) {
            currentConversationIndex.value = 0;
            await loadConversationMsgs(0);
          }
          scrollBottom();
        }
      });
    };

    observeClickOnMsg();
    const loadMessages = () => {
      loadConversationMsgs(currentConversationIndex.value);
    };

    // const addNewMessage = () => {
    //   if (!newMessageText.value) {
    //     return;
    //   }
    //   messages.value.push({
    //     own: true,
    //     type: "out",
    //     image: "media/avatars/150-2.jpg",
    //     time: "Just now",
    //     text: newMessageText.value,
    //     status: "Sent",
    //   });

    //   setTimeout(() => {
    //     if (messagesRef.value) {
    //       messagesRef.value.scrollTop = messagesRef.value.scrollHeight;
    //     }
    //   }, 1);

    //   // newMessageText.value = "";
    //   // setTimeout(() => {
    //   //   messages.value.push({
    //   //     type: "in",
    //   //     name: "Ja Morant",
    //   //     image: "media/avatars/150-15.jpg",
    //   //     time: "Just now",
    //   //     text: "Thank you for your awesome support!",
    //   //   });

    //   //   setTimeout(() => {
    //   //     if (messagesRef.value) {
    //   //       messagesRef.value.scrollTop = messagesRef.value.scrollHeight;
    //   //     }
    //   //   }, 1);
    //   // }, 2000);
    // };
    onUnmounted(() => {
      emitter?.off("sms-list-refresh");
    });

    return {
      title,
      messages,
      messagesRef,
      conversations,
      newMessageText,
      // addNewMessage,
      loadMessages,
      messagesInRef,
      messagesOutRef,
      previousConversation,
      nextConversation,
      currentConversationIndex,
      scrollBottom,
    };
  },
});
